import { THEME } from 'src/config/config'
import { apolloSdk } from 'src/graphql/apolloSdk'
import { IEnum_Solution_Type } from 'src/graphql/generated/hooks'
import { ensureValidIdToken } from 'src/hooks/tokenRefreshGuard'
import { CompareSolution } from 'src/types'

import {
  SolutionDataFragment,
  SolutionDataFragmentWithFollow,
  SolutionFollowFields,
  withSolutionFollowFields,
} from './SolutionDataFragment'

export const getSolutionsBySlugs = async (
  vendors: CompareSolution[],
  solutionType: IEnum_Solution_Type,
): Promise<CompareSolution[]> => {
  const compares: CompareSolution[] = await Promise.all(
    vendors.map(async (item) => {
      const slug = item.slug.split('#').join(',')
      let solutions
      if (solutionType === IEnum_Solution_Type.Product) {
        const result = await apolloSdk.productBySlug({ slug, includeIlta: THEME === 'ilta' })
        solutions = result.solutions
      } else if (solutionType === IEnum_Solution_Type.Consultancy) {
        const result = await apolloSdk.consultancyBySlug({ slug, includeIlta: THEME === 'ilta' })
        solutions = result.solutions
      } else if (solutionType === IEnum_Solution_Type.Alsp) {
        const result = await apolloSdk.alspBySlug({ slug, includeIlta: THEME === 'ilta' })
        solutions = result.solutions
      }

      return {
        id: solutions?.[0]?.documentId || '',
        logo: solutions?.[0]?.logo?.url || '',
        slug,
        solutionType,
      }
    }),
  )

  return compares
}

export const getFullSolutionsBySlugs = async (
  slugs: string[],
  solutionType: IEnum_Solution_Type,
  idToken?: string,
): Promise<SolutionDataFragmentWithFollow[]> => {
  const compares = await Promise.all(
    slugs.map(async (slug) => {
      let solutions
      if (solutionType === IEnum_Solution_Type.Product) {
        const result = await apolloSdk.productBySlug({ slug, includeIlta: THEME === 'ilta' })
        solutions = result.solutions
      } else if (solutionType === IEnum_Solution_Type.Consultancy) {
        const result = await apolloSdk.consultancyBySlug({ slug, includeIlta: THEME === 'ilta' })
        solutions = result.solutions
      } else if (solutionType === IEnum_Solution_Type.Alsp) {
        const result = await apolloSdk.alspBySlug({ slug, includeIlta: THEME === 'ilta' })
        solutions = result.solutions
      }
      const rs = solutions && solutions.length > 0 ? solutions[0] : null

      if (!idToken || !rs) return rs as SolutionDataFragmentWithFollow | null

      const validToken = await ensureValidIdToken()
      if (!validToken) return rs as SolutionDataFragmentWithFollow

      const headers = {
        Authorization: `Bearer ${validToken}`,
        'x-organization-id': THEME === 'ilta' ? 'ilta' : 'lth',
      }
      const resultData = await apolloSdk.searchSolutions(
        {
          query: {
            solutionID: rs.documentId,
            limit: 1,
            offset: 0,
            sort: 'Default',
            subTopic: '',
            followOnly: false,
            includeCompany: true,
          },
        },
        headers,
      )

      const followItem = resultData.searchSolutions?.solutions?.find(
        (item) => item && String(item.documentId) === String(rs.documentId),
      ) as SolutionFollowFields | undefined

      return withSolutionFollowFields(rs as SolutionDataFragment, followItem)
    }),
  )

  return compares.filter((product): product is SolutionDataFragmentWithFollow => !!product)
}
